import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, Building } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    gewerk: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate gewerk field
    if (!formData.gewerk) {
      toast({
        title: "Bitte wählen Sie ein Gewerk",
        description: "Das Gewerk-Feld ist erforderlich.",
        variant: "destructive"
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/contact-inquiry`, formData);
      
      if (response.status === 200 || response.status === 201) {
        toast({
          title: "Anfrage erfolgreich gesendet!",
          description: "Wir melden uns in Kürze bei Ihnen.",
        });
        
        // Reset form
        setFormData({
          company: '',
          name: '',
          email: '',
          phone: '',
          gewerk: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Fehler beim Senden",
        description: "Bitte versuchen Sie es später erneut oder kontaktieren Sie uns telefonisch.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="kontakt" className="py-20 lg:py-32 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-[#8B1538]/10 text-[#8B1538] text-sm font-semibold mb-4">
            Kontakt
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Starten Sie Ihr Projekt mit uns
          </h2>
          <p className="text-xl text-gray-600">
            Senden Sie uns Ihre Anfrage und erhalten Sie ein individuelles Angebot
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Kontaktinformationen
              </h3>
              <p className="text-gray-600 mb-8">
                Kontaktieren Sie uns für eine persönliche Beratung oder ein unverbindliches Angebot.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#8B1538]/10 flex items-center justify-center">
                  <Phone className="w-6 h-6 text-[#8B1538]" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Telefon</div>
                  <a href="tel:+4917912953010" className="text-gray-600 hover:text-[#8B1538] transition-colors">
                    0179 / 12 95 310
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#8B1538]/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#8B1538]" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">E-Mail</div>
                  <a href="mailto:info@kalembaustoffe.de" className="text-gray-600 hover:text-[#8B1538] transition-colors">
                    info@kalembaustoffe.de
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#8B1538]/10 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-[#8B1538]" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Liefergebiet</div>
                  <p className="text-gray-600">
                    Deutschlandweit
                  </p>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                <Building className="w-5 h-5 mr-2 text-[#8B1538]" />
                Geschäftszeiten
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Montag - Freitag</span>
                  <span className="font-semibold text-gray-900">07:00 - 17:00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Samstag</span>
                  <span className="font-semibold text-gray-900">Geschlossen</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Sonntag</span>
                  <span className="font-semibold text-gray-900">Geschlossen</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 lg:p-12 border border-gray-200">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label htmlFor="company" className="text-gray-700 font-semibold mb-2 block">
                    Firma *
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    required
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Ihre Firma"
                    className="h-12 border-gray-300 focus:border-[#8B1538] focus:ring-[#8B1538]"
                  />
                </div>

                <div>
                  <Label htmlFor="name" className="text-gray-700 font-semibold mb-2 block">
                    Ansprechpartner *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Ihr Name"
                    className="h-12 border-gray-300 focus:border-[#8B1538] focus:ring-[#8B1538]"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label htmlFor="email" className="text-gray-700 font-semibold mb-2 block">
                    E-Mail *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="ihre@email.de"
                    className="h-12 border-gray-300 focus:border-[#8B1538] focus:ring-[#8B1538]"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-gray-700 font-semibold mb-2 block">
                    Telefon *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+49 XXX XXXXXXX"
                    className="h-12 border-gray-300 focus:border-[#8B1538] focus:ring-[#8B1538]"
                  />
                </div>
              </div>

              <div className="mb-6">
                <Label htmlFor="gewerk" className="text-gray-700 font-semibold mb-2 block">
                  Gewerk *
                </Label>
                <Select
                  name="gewerk"
                  value={formData.gewerk}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, gewerk: value }))}
                >
                  <SelectTrigger className="h-12 border-gray-300 focus:border-[#8B1538] focus:ring-[#8B1538]">
                    <SelectValue placeholder="Bitte wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hochbau">Hochbau</SelectItem>
                    <SelectItem value="ausbau">Ausbau</SelectItem>
                    <SelectItem value="galabau">Gala-Bau</SelectItem>
                    <SelectItem value="tiefbau">Tiefbau</SelectItem>
                    <SelectItem value="bauelemente">Bauelemente</SelectItem>
                    <SelectItem value="fliesen">Fliesen</SelectItem>
                    <SelectItem value="sonstiges">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-8">
                <Label htmlFor="message" className="text-gray-700 font-semibold mb-2 block">
                  Ihre Nachricht *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Beschreiben Sie Ihr Projekt oder Ihre Anfrage..."
                  rows={6}
                  className="border-gray-300 focus:border-[#8B1538] focus:ring-[#8B1538] resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 bg-[#8B1538] hover:bg-[#6B0F2A] text-white text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50"
              >
                {isSubmitting ? (
                  'Wird gesendet...'
                ) : (
                  <>
                    Anfrage senden
                    <Send className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>

              <p className="text-sm text-gray-500 mt-4 text-center">
                * Pflichtfelder. Ihre Daten werden vertraulich behandelt.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};